/**
 * An alert's fate in its inbox row (PRD-0075 R-B5, ADR-0096): the line that says what the team
 * did with it — accepted into an investigation, dismissed, snoozed until a date, by whom and when —
 * and, for a collaborator, the menu that triages it.
 *
 * Presentational: the feature (`features/monitor`) maps the alert onto `AlertTriageRow` and passes
 * `actions` only to a reader who may triage.
 */
import { BellOff, Clock, FolderInput, MoreHorizontal, RotateCcw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { AlertState } from '@/services/api/case-monitor'
import { formatDate } from '@/utils/date'

const TRIAGE = 'cases.monitor.triage'

/** Every triage state, in the order the inbox filter offers them. */
export const ALERT_STATES: readonly AlertState[] = ['new', 'accepted', 'dismissed', 'snoozed']

const STATE_ICONS = { accepted: FolderInput, dismissed: BellOff, snoozed: Clock } as const

/** How long a snooze lasts, in days, as the menu offers it. */
export const SNOOZE_DAYS = { day: 1, week: 7, month: 30 } as const
export type SnoozeLength = keyof typeof SNOOZE_DAYS

/** What the team did with an alert, as its row shows it. */
export interface AlertTriageRow {
  /** Null for a state this build does not know (a newer server). */
  state: AlertState | null
  byName: string | null
  /** When it was triaged; ISO 8601. */
  at: string | null
  snoozedUntil: string | null
  investigationId: string | null
  investigationTitle: string | null
}

/** Triage of one alert or a group of them (one rule in one source); `label` names what is accepted. */
export interface AlertTriageActions {
  onAccept: (alertIds: readonly string[], label: string) => void
  onDismiss: (alertIds: readonly string[]) => void
  onSnooze: (alertIds: readonly string[], length: SnoozeLength) => void
  onReopen: (alertIds: readonly string[]) => void
  /** A triage is in flight: the menus wait for it. */
  pending: boolean
}

export interface AlertTriageProps {
  /** Opens the investigation an accepted alert went into. */
  onOpenInvestigation: (investigationId: string) => void
  /** Null for a reader who may not triage (a viewer). */
  actions: AlertTriageActions | null
}

function Accepted({ row, onOpen }: { row: AlertTriageRow; onOpen: (id: string) => void }) {
  const { t } = useTranslation()
  const { investigationId, investigationTitle } = row
  if (!investigationId || !investigationTitle) return <>{t(`${TRIAGE}.acceptedIntoDeleted`)}</>
  return (
    <button
      type="button"
      onClick={() => onOpen(investigationId)}
      className="truncate text-foreground/85 underline-offset-2 hover:text-foreground hover:underline"
    >
      {t(`${TRIAGE}.acceptedInto`, { investigation: investigationTitle })}
    </button>
  )
}

/** "Accepted into X · Eva · 28 Oct 2026", "Dismissed · …", "Snoozed until …"; nothing for a new alert. */
export function AlertTriageLine({
  row,
  onOpenInvestigation,
}: {
  row: AlertTriageRow
  onOpenInvestigation: (investigationId: string) => void
}) {
  const { t, i18n } = useTranslation()
  if (row.state === null || row.state === 'new') return null
  const Icon = STATE_ICONS[row.state]
  const what =
    row.state === 'accepted' ? (
      <Accepted row={row} onOpen={onOpenInvestigation} />
    ) : row.state === 'snoozed' && row.snoozedUntil ? (
      t(`${TRIAGE}.snoozedUntil`, { date: formatDate(row.snoozedUntil, i18n.language) })
    ) : (
      t(`${TRIAGE}.states.${row.state}`)
    )
  const who = [row.byName, row.at ? formatDate(row.at, i18n.language) : null].filter(Boolean)
  return (
    <p className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
      <Icon aria-hidden className="size-3.5 shrink-0" />
      <span className="min-w-0 truncate">{what}</span>
      {who.length > 0 ? <span className="shrink-0">· {who.join(' · ')}</span> : null}
    </p>
  )
}

/** An alert a triage menu acts on, by the state it is in. */
export interface TriagedAlert {
  id: string
  state: AlertState | null
}

/** The alerts not yet in `state`: an action takes only the alerts it changes. */
function notIn(alerts: readonly TriagedAlert[], ...states: (AlertState | null)[]): string[] {
  return alerts.filter(alert => !states.includes(alert.state)).map(alert => alert.id)
}

/**
 * The triage menu of a row, for its one alert or its whole group. Each action takes the alerts it
 * changes: an accept leaves the accepted ones in their investigation, a dismiss the dismissed ones,
 * a reopen the new ones; a snooze takes every one (it can push a snooze further).
 */
export function AlertTriageMenu({
  alerts,
  name,
  actions,
}: {
  alerts: readonly TriagedAlert[]
  name: string
  actions: AlertTriageActions
}) {
  const { t } = useTranslation()
  const toAccept = notIn(alerts, 'accepted')
  const toDismiss = notIn(alerts, 'dismissed')
  const toReopen = notIn(alerts, 'new', null)
  const alertIds = alerts.map(alert => alert.id)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          disabled={actions.pending}
          aria-label={t(`${TRIAGE}.menu`, { name })}
        >
          <MoreHorizontal aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {toAccept.length > 0 ? (
          <DropdownMenuItem onSelect={() => actions.onAccept(toAccept, name)}>
            <FolderInput aria-hidden />
            {t(`${TRIAGE}.accept`)}
          </DropdownMenuItem>
        ) : null}
        {toDismiss.length > 0 ? (
          <DropdownMenuItem onSelect={() => actions.onDismiss(toDismiss)}>
            <BellOff aria-hidden />
            {t(`${TRIAGE}.dismiss`)}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Clock aria-hidden />
            {t(`${TRIAGE}.snooze`)}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {(Object.keys(SNOOZE_DAYS) as SnoozeLength[]).map(length => (
              <DropdownMenuItem key={length} onSelect={() => actions.onSnooze(alertIds, length)}>
                {t(`${TRIAGE}.snoozeFor.${length}`)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        {toReopen.length > 0 ? (
          <DropdownMenuItem onSelect={() => actions.onReopen(toReopen)}>
            <RotateCcw aria-hidden />
            {t(`${TRIAGE}.reopen`)}
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
